import { useEffect } from "react";

import { log } from "next-axiom";
import { useTranslation } from "next-i18next";
import Router from "next/router";

import { kebabCase } from "@/common/utils/general.utils";

const IGNORE_KEYS_REGEX =
  /(category|style|brand)-(.*)-(description|seo-title|seo-description)/;

// A page that lost a whole namespace would otherwise send a line per key.
export const MAX_REPORTS_PER_VISIT = 20;

/**
 * Reports each translation key a visitor sees raw on screen to Axiom, once per
 * key per visit (WHO-6644). A `t()` that shows its default text instead is not
 * reported. i18next emits `missingKey` only with `saveMissing` on, which
 * `next-i18next.config.js` sets in the browser; without it this never fires.
 */
const useLogMissingKeys = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!i18n || typeof i18n.on !== "function") {
      return;
    }

    // The browser keeps one i18n instance for the whole visit.
    const reported = new Set();

    const reportMissingKey = (lngs, namespace, key, shownText) => {
      if (!key || shownText !== key) {
        return;
      }

      if (key.match(IGNORE_KEYS_REGEX)) {
        return;
      }

      if (key !== kebabCase(key)) {
        return;
      }

      const id = `${lngs}:${namespace}:${key}`;

      if (reported.has(id) || reported.size >= MAX_REPORTS_PER_VISIT) {
        return;
      }

      reported.add(id);
      log.warn(`Missing translation for key ${key} in ${namespace} namespace`, {
        key,
        namespace,
        locale: lngs?.[0],
        // The route pattern, never the address: a client link carries its token.
        route: Router.pathname,
      });
    };

    i18n.on("missingKey", reportMissingKey);

    return () => i18n.off("missingKey", reportMissingKey);
  }, [i18n]);
};

export default useLogMissingKeys;
